import type { ActionItem } from "@/admin/ActionItem.tsx";
import { updateUserInvoiceFlagMutation } from "@/admin/users/usersQuery.ts";
import type { GQUserItem } from "@/admin/users/UsersTable.tsx";
import { DISABLED } from "@/components/Action.tsx";
import { ActionMenu } from "@/components/ActionMenu.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { EnumBadge, type EnumProps } from "@/components/EnumBadge.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import {
    getAllowInvoiceFlagKey,
    type AllowInvoiceFlagStatus,
    type AllowInvoiceFlagStatusKey,
} from "@warrenio/api-spec/spec/userEnums";

interface AllowInvoiceFlagActionProps {
    value: GQUserItem;
}

export function AllowInvoiceFlagBadge({ value }: { value: AllowInvoiceFlagStatus | undefined }) {
    const key = getAllowInvoiceFlagKey(value ?? null);
    return <EnumBadge options={allowInvoiceFlagDescriptions} value={key} />;
}

export function AllowInvoiceFlagBadgeButton(props: AllowInvoiceFlagActionProps) {
    const { value } = props;

    const updateMutation = useStandardMutation(updateUserInvoiceFlagMutation);
    const onClick = async (flagVal: boolean | null | undefined) => {
        await updateMutation.mutateAsync({
            body: { user_id: Number(value.id), allow_invoice: flagVal },
        });
    };

    const actions: ActionItem[] = [
        {
            id: "set_allow_invoice_flag_true",
            title: "Allow",
            action: value.allow_invoice === true ? DISABLED : () => onClick(true),
        },
        {
            id: "set_allow_invoice_flag_false",
            title: "Disallow",
            action: value.allow_invoice === false ? DISABLED : () => onClick(false),
        },
        {
            id: "unset_allow_invoice_flag",
            title: "Unset flag",
            action: value.allow_invoice === null ? DISABLED : () => onClick(null),
        },
    ];

    return (
        <ActionMenu items={actions}>
            <WButton variant="ghost" {...props} action={undefined}>
                <AllowInvoiceFlagBadge value={value.allow_invoice} />
            </WButton>
        </ActionMenu>
    );
}

const allowInvoiceFlagDescriptions: Record<AllowInvoiceFlagStatusKey, EnumProps> = {
    ALLOWED: {
        label: "Allowed",
        description: "Applying for invoice is allowed for user.",
        color: "success",
    },
    DISALLOWED: {
        label: "Disallowed",
        description: "Applying for invoice is disallowed for user.",
        color: "error",
    },
    NOT_SET: {
        label: "Not Set",
        description: "Applying for invoice preference is not set for user.",
        color: "white",
    },
};
